import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PatientControlButton from 'components/atoms/patentControlButton';
import HoldingControlStatusIcon from 'components/atoms/icons/holdingControlStatusIcon';
import {
  useUpdateConsumablesDate,
  useUpdatePatientHoldingStatus,
  useUpdateTempHoldStatus,
} from 'infrastructure/hooks/control-panel';
import HoldingStatusModal from 'components/molecules/holding-status-modal';
import forms from 'constants/forms';
import { differenceInCalendarDays } from 'date-fns';
import { capitalize } from 'lodash';
import useKey from 'infrastructure/hooks/utils/use-key';
import { useAccess } from 'infrastructure/hooks/access/useAccess';
import { parseDateStringWithoutTimeZoneAdopt } from 'infrastructure/functions';

import type { ModuleType } from 'infrastructure/enums';

interface IProps {
  module: ModuleType;
  patientGuid: string;
}

const HoldingStatusControl: React.FC<IProps> = (props) => {
  const { module, patientGuid } = props;
  const [open, setOpen] = useState(false);
  const { key, changeKey } = useKey();
  const { t } = useTranslation();
  const { patientInfo, isCombinedModule, isReadOnlyRole, isCcmActionDisabled } =
    useAccess({
      module,
      patientGuid,
    });

  const { updateHoldingStatus, holdingStatusLoading } =
    useUpdatePatientHoldingStatus({
      onSuccess() {
        setOpen(false);
        changeKey();
      },
    });
  const { updateTempHoldStatus, tempHoldingStatusLoading } =
    useUpdateTempHoldStatus({
      onSuccess() {
        setOpen(false);
        changeKey();
      },
    });

  const { updateConsumablesDate, consumablesDateLoading } =
    useUpdateConsumablesDate({
      onSuccess() {
        setOpen(false);
        changeKey();
      },
    });

  const loading =
    holdingStatusLoading || tempHoldingStatusLoading || consumablesDateLoading;

  const holdingStatusStartTime = parseDateStringWithoutTimeZoneAdopt(
    patientInfo?.holdingStatus?.startTime,
  );
  const holdingStatusEndTime = parseDateStringWithoutTimeZoneAdopt(
    patientInfo?.holdingStatus?.endTime,
  );

  const remainingDays =
    holdingStatusEndTime &&
    differenceInCalendarDays(holdingStatusEndTime, new Date());

  const currentHoldingStatus =
    patientInfo?.holdingStatus?.status !== 'active' &&
    typeof patientInfo?.holdingStatus?.endTime !== 'undefined' &&
    typeof remainingDays !== 'undefined'
      ? `${remainingDays + 1} Day${remainingDays > 1 ? 's' : ''} ${capitalize(
          patientInfo?.holdingStatus.status,
        )}`
      : '';

  return (
    <>
      <PatientControlButton
        label={t('labels.holdingStatus')}
        Icon={HoldingControlStatusIcon}
        loading={loading}
        status={currentHoldingStatus}
        disabled={isCombinedModule ? isReadOnlyRole : isCcmActionDisabled}
        handleClick={() => setOpen(true)}
      />
      <HoldingStatusModal
        key={key}
        isOpen={open}
        patientGuid={patientGuid}
        toggle={setOpen}
        patientHoldingStatus={patientInfo?.holdingStatus?.status ?? null}
        nextHoldingStatus={patientInfo?.holdingStatus?.nextStatus ?? null}
        holdingStatusStartTime={holdingStatusStartTime}
        holdingStatusEndTime={holdingStatusEndTime}
        updateHoldingStatus={updateHoldingStatus}
        options={forms.patientHoldingStatuses}
        loading={loading}
        module={module}
        ccmHoldingStatus={patientInfo?.ccmHoldingStatus}
        ccmHoldingStatusStartDate={patientInfo?.ccmHoldingStatusStartDate}
        updateTempHoldStatus={updateTempHoldStatus}
        tempHoldingStatusLoading={tempHoldingStatusLoading}
        consumablesArrivingDate={patientInfo?.consumablesArrivingDate}
        updateConsumablesDate={updateConsumablesDate}
        isReadOnly={isReadOnlyRole}
        consumablesDateLoading={consumablesDateLoading}
      />
    </>
  );
};

export default HoldingStatusControl;
